// View Requisition v2 — fast worklist on the V2 API
// (TabColumnsV2, Page/Count keyset paging, TabCounts, {id}/Detail, FilterOptions, SaveColumnsV2, ExportCsv).
// Row / bulk actions reuse the v1 endpoints (see services/actionsApi.ts).
import { Tooltip } from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BulkActionBar, { BulkAction } from "./components/BulkActionBar";
import CollectionInfoModal, {
  CollectionInfo,
} from "./components/CollectionInfoModal";
import ColumnsModal from "./components/ColumnsModal";
import DetailPanel from "./components/DetailPanel";
import EditDocModal from "./components/EditDocModal";
import InlineDocViewer from "./components/InlineDocViewer";
import FilterChips from "./components/FilterChips";
import QuickFind from "./components/QuickFind";
import ShortcutsModal from "./components/ShortcutsModal";
import { FlagPanel, NotesPanel } from "./components/SidePanelViews";
import StatusChangeModal from "./components/StatusChangeModal";
import WorklistGrid from "./components/WorklistGrid";
import WorklistMeta from "./components/WorklistMeta";
import WorklistTabs from "./components/WorklistTabs";
import {
  COLLECTION_NEXT_STEPS,
  DEFAULT_DATE_WINDOW_DAYS,
  FULL_ROW_MENU_TABS,
  NON_SORTABLE_COLUMNS,
  STORAGE_KEYS,
} from "./constants";
import { useTabConfig } from "./hooks/useTabConfig";
import { useTabCounts } from "./hooks/useTabCounts";
import { useWorklist } from "./hooks/useWorklist";
import { useWorklistShortcuts } from "./hooks/useWorklistShortcuts";
import actionsApi, { ActionError, PrinterInfo } from "./services/actionsApi";
import worklistApi from "./services/worklistApi";
import {
  ActionItem,
  ColumnConfig,
  Row,
  SortState,
  StatusAction,
  WorklistFilter,
} from "./types";
import {
  FilterValue,
  defaultDateFilter,
  pick,
  rowKey,
  rowText,
  storage,
  toRequestFilter,
} from "./utils";
import "./ViewRequisitionV2.css";
import InsuranceDataUpdateModal from "Pages/Requisition/SingleRequisition/ViewReq/InsuranceUpdateModal";
import { savePdfUrls } from "Redux/Actions/Index";
import { Loader } from "Shared/Common/Loader";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { checkPermissions } from "Utils/Common/CommonMethods";
import { PortalTypeEnum } from "Utils/Common/Enums/Enums";

const errorMessage = (e: unknown) =>
  e instanceof ActionError
    ? e.message
    : (e as AxiosError<{ message?: string }>)?.response?.data?.message;

// Side panel content: details (default) or one of these, tied to the row it was opened for.
type SideView =
  | { rowId: number; kind: "doc"; url: string }
  | { rowId: number; kind: "notes" }
  | { rowId: number; kind: "flag"; flagId: number };

const ViewRequisitionV2 = () => {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const menus = useSelector((state: any) => state.Reducer.Menus);
  const isAdmin =
    useSelector(
      (state: any) =>
        state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.portalType
    ) === PortalTypeEnum.Admin;

  const {
    tabs,
    loading: tabsLoading,
    error: tabsError,
    saveColumns,
  } = useTabConfig();
  const { counts, updatedAt, refresh: refreshCounts } = useTabCounts();

  const [tabId, setTabId] = useState<number | null>(null);
  const [filters, setFilters] = useState<WorklistFilter[]>([]);
  const [sort, setSort] = useState<SortState | null>(null);
  const [quickCol, setQuickCol] = useState<number | null>(null);
  const [quickText, setQuickText] = useState("");
  const [quickActive, setQuickActive] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openRow, setOpenRow] = useState<Row | null>(null);
  const [sideView, setSideView] = useState<SideView | null>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [showColumns, setShowColumns] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Status change (bulk or single-row delete) and the rows it applies to.
  const [statusTarget, setStatusTarget] = useState<{
    action: StatusAction;
    rows: Row[];
  } | null>(null);
  const [showEditDoc, setShowEditDoc] = useState(false);
  const [collectionRow, setCollectionRow] = useState<Row | null>(null);
  const [insuranceRow, setInsuranceRow] = useState<Row | null>(null);
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);
  // TabId the current filters belong to: rows only load once filters match the active tab.
  const [filtersFor, setFiltersFor] = useState<number | null>(null);

  const quickFindRef = useRef<HTMLInputElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const tab = tabs.find((x) => x.tabId === tabId) ?? null;
  const visibleCols = useMemo(
    () => tab?.columns.filter((c) => c.isShow && c.isShowOnUi && !c.isExpand) ?? [],
    [tab]
  );
  const dateCol = tab?.columns.find((c) => c.filterType === "daterange");
  const quickCols = useMemo(
    () =>
      tab?.columns.filter(
        (c) => c.filterType === "exact" || c.filterType === "prefix"
      ) ?? [],
    [tab]
  );

  // Label printers (GetPrintersInfo) for the bulk + per-row Print Label menus.
  useEffect(() => {
    actionsApi
      .printers()
      .then(setPrinters)
      .catch(() => setPrinters([]));
  }, []);

  // Pick the saved (or first) tab once config arrives.
  useEffect(() => {
    if (!tabs.length || tabId !== null) return;
    const saved = storage.get<number | null>(STORAGE_KEYS.tab, null);
    setTabId(tabs.some((x) => x.tabId === saved) ? saved : tabs[0].tabId);
  }, [tabs, tabId]);

  // Reset filters/sort when the tab changes; apply the default date window.
  useEffect(() => {
    if (!tab) return;
    setFilters(defaultDateFilter(dateCol, DEFAULT_DATE_WINDOW_DAYS));
    setSort(null);
    setSelected(new Set());
    setOpenRow(null);
    setFocusIdx(-1);
    setQuickActive(false);
    setQuickText("");
    setQuickCol(quickCols[0]?.id ?? null);
    setFiltersFor(tab.tabId);
    storage.set(STORAGE_KEYS.tab, tab.tabId);
  }, [tab?.tabId]);

  const {
    rows,
    hasMore,
    loading,
    count,
    error,
    loadMore,
    reload,
    newRowsAvailable,
    capped,
    currentRequest,
  } = useWorklist(tabId, filters, sort, !!tab && filtersFor === tabId);

  // New result set → clear selection/focus and scroll to top.
  useEffect(() => {
    setSelected(new Set());
    setFocusIdx(-1);
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [currentRequest]);

  // An identifier search that finds exactly one requisition opens it.
  useEffect(() => {
    if (quickActive && !loading && rows.length === 1) setOpenRow(rows[0]);
  }, [quickActive, loading, rows]);

  // ------------------------------------------------------------------ handlers
  const changeTab = (next: number) => {
    if (next === tabId) return;
    setOpenRow(null);
    setSelected(new Set());
    setTabId(next);
  };

  const setColumnFilter = useCallback(
    (c: ColumnConfig, v: FilterValue | null) => {
      setFilters((fs) => [
        ...fs.filter((f) => f.id !== c.id),
        ...(v ? [toRequestFilter(c, v)] : []),
      ]);
    },
    []
  );

  // Identifier search: replaces other filters (incl. date window) — index seek across all dates.
  const applyQuick = () => {
    const v = quickText.trim();
    const col = quickCols.find((c) => c.id === quickCol);
    if (!v || !col) return clearQuick();
    setFilters([toRequestFilter(col, { value: v })]);
    setQuickActive(true);
  };
  const clearQuick = () => {
    setQuickActive(false);
    setQuickText("");
    setFilters(defaultDateFilter(dateCol, DEFAULT_DATE_WINDOW_DAYS));
  };

  const onHeaderClick = (c: ColumnConfig) => {
    if (!c.key || NON_SORTABLE_COLUMNS.includes(c.key.toLowerCase())) return;
    setSort((s) =>
      !s || s.key !== c.key
        ? { key: c.key, dir: "desc" }
        : s.dir === "desc"
          ? { key: c.key, dir: "asc" }
          : null
    );
  };

  const refreshAll = () => {
    reload();
    refreshCounts(true);
    setDetailRefreshKey((k) => k + 1);
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(rowKey(r))),
    [rows, selected]
  );

  // Runs an action with the portal's toast conventions; returns false on failure.
  const run = async (
    fn: () => Promise<string | void | undefined>,
    success = "Request Succesfully Processed"
  ) => {
    try {
      const message = await fn();
      toast.success(t(message || success));
      return true;
    } catch (e) {
      toast.error(t(errorMessage(e) || "Something went wrong"));
      return false;
    }
  };

  // ---------------------------------------------------------------- next step
  const handleNextStep = async (row: Row, file?: File) => {
    const step = rowText(row, "NextStep").toLowerCase();
    // v1: collection steps need date of collection + accession # first.
    if (
      !file &&
      COLLECTION_NEXT_STEPS.includes(step) &&
      (!rowText(row, "DateofCollection") || !rowText(row, "AccessionNumber"))
    )
      return setCollectionRow(row);
    setBusyId(rowKey(row));
    if (await run(() => worklistApi.nextStep(row, file))) refreshAll();
    setBusyId(null);
  };

  const saveCollectionInfo = async (row: Row, values: CollectionInfo) => {
    const done = await run(async () => {
      await actionsApi.updateCollectionInfo(row, values);
      await worklistApi.nextStep(row);
    });
    if (done) {
      setCollectionRow(null);
      refreshAll();
    }
  };

  // ---------------------------------------------------------------- status changes
  const submitStatusChange = async (reason: string, rejectComment?: string) => {
    if (!statusTarget) return;
    const { action, rows: targetRows } = statusTarget;
    const done = await run(async () => {
      await worklistApi.changeStatus({
        statusId: action,
        RequisitionOrderIds: targetRows.map(rowKey),
        ActionReasons: reason,
        rejectComment,
      });
      if (action === StatusAction.Reject)
        await actionsApi.saveRejectionAssignment(targetRows, reason);
    });
    if (done) {
      setStatusTarget(null);
      setSelected(new Set());
      refreshAll();
    }
  };

  // ---------------------------------------------------------------- exports
  // v1 Export_To_ExcelV2 — all rows of the current tab/filters, or only the selected ones.
  const exportExcel = async (selectedOnly: boolean) => {
    if (!tab) return;
    await run(() =>
      actionsApi.exportExcel({
        tabId: tab.tabId,
        sortColumn: sort?.key ?? "",
        sortDirection: sort?.dir ?? "",
        filters,
        ...(selectedOnly ? { selectedRow: selectedRows.map(rowKey) } : {}),
      })
      , "Export ready");
  };

  // ---------------------------------------------------------------- printing
  const printLabels = (printer: PrinterInfo, targetRows: Row[]) =>
    actionsApi
      .printLabels(printer, targetRows)
      .catch((e) => toast.error(t(errorMessage(e) || "Something went wrong")));

  // Records / reports open in the portal docs viewer (same as v1).
  const printDocuments = async (
    fn: (r: Row[]) => Promise<{ message?: string; data: string | string[] }>
  ) => {
    try {
      const res = await fn(selectedRows);
      toast.success(t(res.message || "Request Succesfully Processed"));
      dispatch(savePdfUrls(res.data));
      setSelected(new Set());
      window.open("/docs-viewer", "_blank");
    } catch (e) {
      toast.error(t(errorMessage(e) || "Something went wrong"));
    }
  };

  const handleBulk = async (action: BulkAction) => {
    switch (action) {
      case "clear":
        return setSelected(new Set());
      case "exportAll":
        return exportExcel(false);
      case "exportExcel":
        return exportExcel(true);
      case "transferZoho":
        if (await run(() => actionsApi.transferToZoho(selectedRows))) {
          setSelected(new Set());
          refreshAll();
        }
        return;
      case "editDoc":
        return setShowEditDoc(true);
      case "printRecords":
        return printDocuments(actionsApi.printRecords);
      case "printReports":
        return printDocuments(actionsApi.printReports);
      case "printRecordsAndReports":
        return printDocuments(actionsApi.printRecordsAndReports);
      default:
        return setStatusTarget({ action, rows: selectedRows });
    }
  };

  const saveDateOfCollection = async (date: string) => {
    const done = await run(() =>
      actionsApi.updateDateOfCollection(selectedRows, date)
    );
    if (done) {
      setShowEditDoc(false);
      setSelected(new Set());
      refreshAll();
    }
  };

  // ---------------------------------------------------------------- row actions (v1 ⋮ menu)
  const viewOrder = (row: Row) => {
    // v1 copies the session into localStorage so the new tab is signed in.
    const userinfo = sessionStorage.getItem("userinfo");
    if (userinfo) localStorage.setItem("userinfo", userinfo);
    window.open(
      `/OrderView/${btoa(rowText(row, "RequisitionId"))}/${btoa(String(rowKey(row)))}`,
      "_blank",
      "noreferrer"
    );
  };

  const rowMenuItems = (row: Row): ActionItem[] => {
    const view: ActionItem = {
      key: "view",
      label: "View",
      icon: "fa fa-eye text-success",
      permission: "View",
      onClick: () => viewOrder(row),
    };
    if (!tab || !FULL_ROW_MENU_TABS.includes(tab.tabId))
      return [
        view,
        {
          key: "restore",
          label: "Restore",
          icon: "fa fa-refresh text-success",
          permission: "Restore",
          onClick: async () => {
            if (await run(() => actionsApi.restore(row))) refreshAll();
          },
        },
      ];
    return [
      view,
      {
        key: "edit",
        label: "Edit",
        icon: "fa fa-edit text-warning",
        permission: "Edit",
        onClick: () =>
          navigate("/requisition", {
            state: {
              reqId: pick(row, "RequisitionId"),
              status: pick(row, "RequisitionStatus"),
              orderid: rowKey(row),
              recordid: pick(row, "RecordId"),
            },
          }),
      },
      {
        key: "duplicate",
        label: "Duplicate",
        icon: "fa fa-copy text-muted",
        permission: "Duplicate",
        onClick: async () => {
          try {
            const res = await actionsApi.duplicate(row);
            toast.success(t(res.message || "Requisition duplicated"));
            navigate("/requisition", {
              state: { reqId: res.requisitionId, orderid: res.requisitionOrderId },
            });
          } catch (e) {
            toast.error(t(errorMessage(e) || "Something went wrong"));
          }
        },
      },
      ...(isAdmin
        ? [
          {
            key: "history",
            label: "Requisition History",
            icon: "fa fa-history text-muted",
            permission: "history",
            onClick: () =>
              window.open(
                `/requisition-tracking/${btoa(String(rowKey(row)))}`,
                "_blank",
                "noreferrer"
              ),
          },
        ]
        : []),
      {
        key: "delete",
        label: "Delete",
        icon: "fa fa-trash text-danger",
        danger: true,
        permission: "Delete",
        onClick: () => setStatusTarget({ action: StatusAction.Delete, rows: [row] }),
      },
      {
        key: "print",
        label: "Print",
        icon: "fa fa-print text-info",
        permission: "PrintRecord",
        onClick: () => run(() => actionsApi.printSingleRecord(row)),
      },
      {
        key: "insurance",
        label: "Update Insurance",
        icon: "fa fa-shield text-info",
        permission: "UpdateInsurance",
        onClick: () => setInsuranceRow(row),
      },
    ];
  };

  const toggleRow = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSelected =
    rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map(rowKey)));

  const openRowAt = (row: Row, index: number) => {
    setFocusIdx(index);
    setOpenRow(row);
  };

  // Side panel views (PDF, internal notes, flag) for a row; ✕ returns to its details.
  const openFile = (row: Row, url: string) => {
    setOpenRow(row);
    setSideView({ rowId: rowKey(row), kind: "doc", url });
  };
  const openNotes = (row: Row) => {
    setOpenRow(row);
    setSideView({ rowId: rowKey(row), kind: "notes" });
  };
  const openFlag = (row: Row, flagId: number) => {
    setOpenRow(row);
    setSideView({ rowId: rowKey(row), kind: "flag", flagId });
  };
  // A view only applies to the row it was opened for; selecting another row shows its details.
  const activeView =
    openRow && sideView && sideView.rowId === rowKey(openRow) ? sideView : null;

  const anyModalOpen =
    showColumns ||
    showHelp ||
    !!statusTarget ||
    showEditDoc ||
    !!collectionRow ||
    !!insuranceRow;

  // ------------------------------------------------------------------ keyboard
  useWorklistShortcuts({
    enabled: !anyModalOpen,
    onFocusQuickFind: () => quickFindRef.current?.focus(),
    onHelp: () => setShowHelp(true),
    onSwitchTab: (i) => tabs[i] && changeTab(tabs[i].tabId),
    onMoveFocus: (delta) => {
      if (!rows.length) return;
      const next = Math.max(0, Math.min(rows.length - 1, focusIdx + delta));
      setFocusIdx(next);
      scrollerRef.current
        ?.querySelector(`[data-row-index="${next}"]`)
        ?.scrollIntoView({ block: "nearest" });
      if (openRow) setOpenRow(rows[next]);
      if (next >= rows.length - 5) loadMore();
    },
    onOpenFocused: () => rows[focusIdx] && setOpenRow(rows[focusIdx]),
    onToggleSelectFocused: () => rows[focusIdx] && toggleRow(rowKey(rows[focusIdx])),
    onEscape: () => {
      if (activeView) setSideView(null);
      else if (openRow) setOpenRow(null);
      else if (quickActive) clearQuick();
    },
  });

  // ------------------------------------------------------------------ render
  const sortCol = sort ? tab?.columns.find((c) => c.key === sort.key) : undefined;
  const openId = openRow ? rowKey(openRow) : null;

  const backToDetails = () => setSideView(null);
  const detailPanel =
    openRow &&
    tab &&
    (activeView?.kind === "doc" ? (
      <InlineDocViewer
        url={activeView.url}
        title={
          rowText(openRow, "AccessionNumber") || rowText(openRow, "Order")
        }
        onClose={backToDetails}
      />
    ) : activeView?.kind === "notes" ? (
      <NotesPanel row={openRow} onClose={backToDetails} />
    ) : activeView?.kind === "flag" ? (
      <FlagPanel row={openRow} flagId={activeView.flagId} onClose={backToDetails} />
    ) : (
      <DetailPanel
        row={openRow}
        tabId={tab.tabId}
        columns={tab.columns}
        refreshKey={detailRefreshKey}
        busy={busyId === openId}
        onClose={() => {
          setOpenRow(null);
          setSideView(null);
        }}
        onNextStep={handleNextStep}
        onOpenFile={(url) => openFile(openRow, url)}
        onView={viewOrder}
      />
    ));

  const renderContent = () => {
    if (tabsLoading && !tabs.length) return <Loader />;
    if (tabsError)
      return (
        <div className="alert alert-danger" role="alert">
          {t(tabsError)}
        </div>
      );
    if (!tab)
      return (
        <div className="alert alert-warning">
          {t("No tabs are configured for this page.")}
        </div>
      );

    return (
      <div className="mb-5">
        <WorklistTabs
          tabs={tabs}
          value={tab.tabId}
          counts={counts}
          onChange={changeTab}
        />

        <div className="card tab-content-card">
          <div className="card-body px-3 px-md-8 py-4 d-flex flex-column gap-3">
            <QuickFind
              ref={quickFindRef}
              columns={quickCols}
              columnId={quickCol}
              value={quickText}
              onColumnChange={setQuickCol}
              onValueChange={setQuickText}
              onApply={applyQuick}
              onClear={clearQuick}
            />

            {quickActive ? (
              <div className="vr2-searchnote">
                <span>
                  {t("Searching")} &ldquo;<b>{quickText}</b>&rdquo;{" "}
                  {t("across all dates. Other filters are paused.")}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={clearQuick}
                >
                  {t("Clear search")}
                </button>
              </div>
            ) : (
              <FilterChips
                tabId={tab.tabId}
                columns={tab.columns}
                filters={filters}
                onChange={setColumnFilter}
              />
            )}

            {newRowsAvailable && (
              <div className="vr2-searchnote">
                <span>{t("New requisitions are available.")}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={refreshAll}
                >
                  {t("Refresh")}
                </button>
              </div>
            )}

            <WorklistMeta
              count={count}
              loadedCount={rows.length}
              sortLabel={
                sort ? `${t(sortCol?.label ?? sort.key)} ${sort.dir}` : null
              }
              countsUpdatedAt={updatedAt}
              onRefreshCounts={() => refreshCounts(true)}
            />

            {error && (
              <div className="alert alert-danger py-2 mb-0" role="alert">
                {t(error)}{" "}
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={reload}
                >
                  {t("Retry")}
                </button>
              </div>
            )}

            {/* Grid | detail split; drag the handle to resize (sizes are remembered). */}
            <div className="vr2-body">
              <PanelGroup direction="horizontal" autoSaveId="viewReqV2.split">
                <Panel id="vr2-grid" order={1} minSize={35}>
                  <WorklistGrid
                    columns={visibleCols}
                    rows={rows}
                    loading={loading}
                    hasMore={hasMore}
                    capped={capped}
                    error={!!error}
                    sort={sort}
                    selected={selected}
                    openId={openId}
                    focusIdx={focusIdx}
                    busyId={busyId}
                    canRemoveDateWindow={
                      !!dateCol && filters.some((f) => f.id === dateCol.id)
                    }
                    scrollerRef={scrollerRef}
                    bulkBar={
                      <BulkActionBar
                        selectedCount={selected.size}
                        tabId={tab.tabId}
                        printers={printers}
                        onAction={handleBulk}
                        onPrintLabels={(p) => printLabels(p, selectedRows)}
                      />
                    }
                    onSort={onHeaderClick}
                    onToggleRow={toggleRow}
                    onToggleAll={toggleAll}
                    onRowClick={openRowAt}
                    onNextStep={handleNextStep}
                    onOpenFile={openFile}
                    rowMenuItems={rowMenuItems}
                    printers={printers}
                    onPrintLabel={(row, p) => printLabels(p, [row])}
                    onOpenNotes={openNotes}
                    onOpenFlag={openFlag}
                    onLoadMore={loadMore}
                    onRemoveDateWindow={() =>
                      dateCol &&
                      setFilters((fs) => fs.filter((f) => f.id !== dateCol.id))
                    }
                  />
                </Panel>
                {openRow && !isMobile && (
                  <>
                    <PanelResizeHandle className="vr2-resize-handle">
                      <span className="vr2-resize-grip" />
                    </PanelResizeHandle>
                    <Panel
                      id="vr2-detail"
                      order={2}
                      defaultSize={30}
                      minSize={20}
                      maxSize={60}
                    >
                      {detailPanel}
                    </Panel>
                  </>
                )}
              </PanelGroup>
            </div>
            {/* Small screens: detail slides over the grid instead of splitting it. */}
            {openRow && isMobile && detailPanel}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {tab && (
        <ColumnsModal
          show={showColumns}
          columns={tab.columns}
          onClose={() => setShowColumns(false)}
          onSave={async (ordered) => {
            await saveColumns(tab.tabId, ordered);
            reload();
            toast.success(t("Column layout saved for this tab"));
          }}
        />
      )}
      <ShortcutsModal show={showHelp} onClose={() => setShowHelp(false)} />
      <StatusChangeModal
        action={statusTarget?.action ?? null}
        rows={statusTarget?.rows ?? []}
        onClose={() => setStatusTarget(null)}
        onSubmit={submitStatusChange}
      />
      <EditDocModal
        show={showEditDoc}
        rows={selectedRows}
        onClose={() => setShowEditDoc(false)}
        onSave={saveDateOfCollection}
      />
      <CollectionInfoModal
        row={collectionRow}
        onClose={() => setCollectionRow(null)}
        onSave={saveCollectionInfo}
      />
      {insuranceRow && (
        <InsuranceDataUpdateModal
          open
          rowData={insuranceRow}
          onClose={() => setInsuranceRow(null)}
          loadGridData={refreshAll}
        />
      )}

      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-toolbar py-2 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <Tooltip title={t("Keyboard shortcuts")} arrow placement="top">
                <button
                  id="ViewRequisitionV2ButtonShortcuts"
                  className="btn btn-icon btn-sm fw-bold btn-light-primary"
                  onClick={() => setShowHelp(true)}
                >
                  <i className="bi bi-keyboard fs-4"></i>
                </button>
              </Tooltip>
              <Tooltip title={t("Setup")} arrow placement="top">
                <button
                  id="ViewRequisitionV2ButtonSetup"
                  className="btn btn-icon btn-sm fw-bold btn-setting btn-icon-light"
                  disabled={!tab}
                  onClick={() => setShowColumns(true)}
                >
                  <i className="fa fa-gear"></i>
                </button>
              </Tooltip>
              {checkPermissions(menus, "/requisition") && (
                <PermissionComponent
                  moduleName="Requisition"
                  pageName="View Requisition v2"
                  permissionIdentifier="CreateRequisition"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <Link
                      id="ViewRequisitionV2CreateButton"
                      className="btn btn-sm fw-500 btn-primary"
                      to="/requisition"
                    >
                      <i className="bi bi-plus-lg"></i>
                      {getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Create Requisition"
                      )}
                    </Link>
                  )}
                </PermissionComponent>
              )}
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div
            id="kt_app_content_container"
            className="app-container container-fluid"
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewRequisitionV2;
